import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';

const API_BASE_URL = '/api/v1';

type VerifyState = 'working' | 'success' | 'invalid';

export default function VerifyDemoRequestPage() {
  const { t } = useTranslation('demo');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<VerifyState>('working');

  useEffect(() => {
    let cancelled = false;

    async function verify(): Promise<void> {
      if (!token) {
        setState('invalid');
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/public/demo-requests/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        setState(response.ok ? 'success' : 'invalid');
      } catch {
        if (!cancelled) setState('invalid');
      }
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            {t('verifyTitle')}
          </Typography>
          {state === 'working' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} role="status">
              <CircularProgress size={24} />
              <Typography>{t('verifyWorking')}</Typography>
            </Box>
          ) : null}
          {state === 'success' ? (
            <Alert severity="success" role="status">
              <Typography fontWeight={600}>{t('verifySuccessTitle')}</Typography>
              <Typography variant="body2">{t('verifySuccessBody')}</Typography>
            </Alert>
          ) : null}
          {state === 'invalid' ? (
            <Alert severity="error" role="alert">
              <Typography fontWeight={600}>{t('verifyInvalidTitle')}</Typography>
              <Typography variant="body2">{t('verifyInvalidBody')}</Typography>
            </Alert>
          ) : null}
          <Button component={RouterLink} to="/request-demo" sx={{ mt: 3 }}>
            {t('backToForm')}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {t('providerNote')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
